import * as React from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { translate } from '~l10n/client';
import { toast } from '~shared/components/Toast';
import { workspaceKeys } from '~teacher/hook/workspaces/workspaceQueryKeyFactory';

import {
  MembersSortBy,
  MembersSortDir,
  getWorkspaceMembers,
} from '../../../api/workspaces/getWorkspaceMembers';

export const DEFAULT_MEMBERS_SORT_BY: MembersSortBy = 'role';
export const DEFAULT_MEMBERS_SORT_DIR: MembersSortDir = 'asc';

export function useWorkspaceMembers({
  workspaceId,
  enabled = true,
  sortBy = DEFAULT_MEMBERS_SORT_BY,
  sortDir = DEFAULT_MEMBERS_SORT_DIR,
  search,
}: {
  workspaceId: string;
  enabled?: boolean;
  sortBy?: MembersSortBy;
  sortDir?: MembersSortDir;
  search?: string;
}) {
  const trimmedSearch = search?.trim() || undefined;

  const query = useInfiniteQuery({
    // `search` is part of the key so a new term refetches from the first page
    // rather than appending to the previous result set.
    queryKey: workspaceKeys.membersSorted({
      workspaceId,
      sortBy,
      sortDir,
      search: trimmedSearch,
    }),
    queryFn: ({ pageParam }) =>
      getWorkspaceMembers(workspaceId, {
        cursor: pageParam as string,
        sortBy,
        sortDir,
        search: trimmedSearch,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
    keepPreviousData: true,
    // Surface fetch failures as the standard connection-error toast.
    // The table just renders empty on error.
    onError: () => {
      toast(
        translate(
          'An error occurred. Please check your connection and try again.'
        ),
        { tone: 'critical' }
      );
    },
  });

  const members = React.useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.members),
    [query.data]
  );

  const limits = query.data?.pages?.[0]?.limits;

  return {
    ...query,
    members,
    limits,
  };
}
